import '.d{display:flex;}.ssr-plugin'
import '.fe{justify-content:flex-start;}.ssr-plugin'
import '.fi{width:1.25rem;}.ssr-plugin'
import '.fj{height:1.25rem;}.ssr-plugin'
import '.fk{border-radius:50%;}.ssr-plugin'
import '.fl{border:2px solid var(--gray-300);}.ssr-plugin'
import '.am{flex-shrink:0;}.ssr-plugin'
import { useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { CardWrapper } from '@app/components/blocks/card/CardWrapper'
import { styled } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import { useMemo } from 'react'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { useTranslations } from '@i18n/useLanguage'
import { CtaCard } from '@app/components/blocks/card/CtaCard'
import personHoldingNutScrew from '@app/assets/images/person_holding_a_nut_screw.svg'
import { Image } from '@app/components/core/image/Image'

import type {
  RawPlotRegistryInitiativeItem,
  PlotRegistryUserInitiative,
} from 'functions/app/plot-registry/list-user-initiatives'

type SelectInitiativeStepProps = {
  plotRegistryId: number
  userId: number
  selectedInitiative: PlotRegistryUserInitiative | null
  onSelect: (initiative: PlotRegistryUserInitiative) => void
}

export const SelectInitiativeStep = ({
  plotRegistryId,
  userId,
  selectedInitiative,
  onSelect,
}: SelectInitiativeStepProps) => {
  const isMobile = useMediaQuery()
  const t = useTranslations().app.plotRegistry.selectInitiative
  const { data: rawData } = useQuery(
    'list-plot-registry-user-initiatives',
    { plotRegistryId, userId },
  )

  const initiatives = useMemo<PlotRegistryUserInitiative[]>(() => {
    if (!rawData || !Array.isArray(rawData)) {
      return []
    }
    return (rawData as RawPlotRegistryInitiativeItem[])
      .filter((item) =>
        (item.users ?? []).some(
          (user) =>
            user.id === userId && (user.$role === 'owner' || user.$role === 'admin'),
        ),
      )
      .map((item) => {
      const existingApp = (item.plotRegistryApplications ?? []).find(
        (app: { id: number; plotRegistry?: { id: number } }) => app.plotRegistry?.id === plotRegistryId,
      )
      return {
        id: item.id,
        title: item.title,
        description: item.description,
        image: item.images?.[0],
        hasExistingApplication: !!existingApp,
        existingApplicationId: existingApp?.id,
      }
    })
  }, [rawData, plotRegistryId, userId])

  if (!rawData) {
    return <LoaderWrapper loading={true}>{null}</LoaderWrapper>
  }

  const hasInitiatives = initiatives.length > 0

  return (
    <Row
      gap="3rem"
      style={{
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: '100vh',
        justifyContent: 'center',
      }}
    >
      <Column
        gap="0.5rem"
        style={{
          flex: '1 1 0',
          minWidth: 0,
          width: isMobile ? '100%' : '50%',
          maxWidth: '100%',
        }}
      >
        <div className="d fe">
        <CtaCard
          title=''
          illustration={personHoldingNutScrew}
        />
        </div>
        <Text.Small
          style={{
            color: 'var(--palette-red-400)',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {t.stepLabel}
        </Text.Small>
        <Title.H3>{t.title}</Title.H3>
        <Text.Medium style={{ color: 'var(--gray-500)' }}>
          {t.subtitle}
        </Text.Medium>
      </Column>

      <Column
        gap="0.75rem"
        style={{
          flex: '1 1 0',
          minWidth: 0,
          width: isMobile ? '100%' : '50%',
          maxWidth: '100%',
        }}
      >
        {hasInitiatives &&
          initiatives.map((initiative) => {
          const isSelected = selectedInitiative?.id === initiative.id
          return (
            <CardWrapper
              key={initiative.id}
              onClick={() => onSelect(initiative)}
              style={{
                border: isSelected
                  ? '1px solid var(--brand-secondary)'
                  : 'var(--border-default)',
                cursor: 'pointer',
              }}
            >
              <Row gap="0.75rem" style={{ alignItems: 'center' }}>
                {initiative.image?.src && (
                  <Image
                    src={initiative.image.src}
                    alt={initiative.title}
                    resizeWidth={56}
                    style={{
                      width: '3.5rem',
                      height: '3.5rem',
                      borderRadius: '0.5rem',
                      objectFit: 'cover',
                    }}
                  />
                )}
                <Column gap="0.25rem" style={{ flex: 1 }}>
                  <Text.Medium isBold>{initiative.title}</Text.Medium>
                  {initiative.description && (
                    <Text.Small
                      style={{
                        color: 'var(--gray-500)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {initiative.description}
                    </Text.Small>
                  )}
                </Column>
                <div className="fi fj fk fl am"
                  style={{
                    backgroundColor: isSelected
                      ? 'var(--brand-secondary)'
                      : 'transparent',}}
                />
              </Row>
            </CardWrapper>
          )
        })}
      </Column>
    </Row>
  )
}
